import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Users, Clock, TrendingUp, Briefcase, FileDown } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const AdminStatsView = ({ 
  stats, 
  repartitionReferents, 
  onShowSyntheseGenerator, 
  onDownloadSyntheseCollective 
}) => {
  return (
    <>
      <div className="flex justify-end gap-3 mb-4">
        <Button
          onClick={onShowSyntheseGenerator}
          variant="outline"
          className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50"
        >
          <Users className="w-4 h-4 mr-2" />
          Synthèses individuelles/groupées
        </Button>
        <Button
          onClick={onDownloadSyntheseCollective}
          data-testid="download-synthese-collective-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl"
        >
          <FileDown className="w-4 h-4 mr-2" />
          Synthèse collective
        </Button>
      </div>

      {/* Cartes KPI */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-blue-50 border-blue-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-blue-700">
                Bénéficiaires Actifs
              </CardTitle>
              <Users className="w-8 h-8 text-blue-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div data-testid="bénéficiaires-count" className="text-4xl font-extrabold text-blue-900">
              {stats?.apprenants_actifs || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-amber-50 border-amber-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-amber-700">
                Total Heures
              </CardTitle>
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div data-testid="total-heures" className="text-4xl font-extrabold text-amber-900">
              {stats?.total_heures || 0}h
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-50 border-emerald-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-emerald-700">
                Taux de Placement
              </CardTitle>
              <TrendingUp className="w-8 h-8 text-emerald-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div data-testid="taux-placement" className="text-4xl font-extrabold text-emerald-900">
              {stats?.taux_placement || 0}%
            </div>
          </CardContent>
        </Card>

        <Card className="bg-rose-50 border-rose-100 rounded-3xl p-6 shadow-sm hover:-translate-y-1 hover:shadow-md transition-all duration-300">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-bold uppercase tracking-wider text-rose-700">
                Contrats Signés
              </CardTitle>
              <Briefcase className="w-8 h-8 text-rose-600" />
            </div>
          </CardHeader>
          <CardContent>
            <div data-testid="contrats-count" className="text-4xl font-extrabold text-rose-900">
              {(stats?.cdi_count || 0) + (stats?.cdd_count || 0)}
            </div>
            <div className="text-sm text-rose-700 mt-2">
              {stats?.cdi_count || 0} CDI • {stats?.cdd_count || 0} CDD
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Graphique répartition */}
      <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm mt-6">
        <CardHeader>
          <CardTitle className="text-xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
            Répartition des Bénéficiaires par Référent
          </CardTitle>
        </CardHeader>
        <CardContent>
          {repartitionReferents.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={repartitionReferents}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="nom" tick={{ fontSize: 12 }} />
                  <YAxis label={{ value: 'Nombre de bénéficiaires', angle: -90, position: 'insideLeft' }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="count" name="Bénéficiaires" fill="#2563EB" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 grid grid-cols-2 gap-2">
                {repartitionReferents.map((ref, idx) => (
                  <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-xl">
                    <span className="text-sm font-medium text-slate-700">{ref.nom}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-900">{ref.count}/45</span>
                      <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                        ref.pourcentage >= 90 ? 'bg-red-100 text-red-700' :
                        ref.pourcentage >= 70 ? 'bg-amber-100 text-amber-700' :
                        'bg-emerald-100 text-emerald-700'
                      }`}>
                        {ref.pourcentage}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="text-center text-slate-500 py-8">
              Aucun référent avec bénéficiaires
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default AdminStatsView;
